import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLD from "@/components/feature/JsonLD";

const timelineEvents = [
  {
    year: "2020",
    title: "平台创立",
    description: "一群热爱动漫的年轻人创立了奶水动漫在线，怀着让更多人享受动漫快乐的初心，开启了这段旅程。",
  },
  {
    year: "2021",
    title: "资源扩充",
    description: "平台动漫资源突破5000部，覆盖热血、恋爱、搞笑、奇幻等多个品类，满足不同喜好的动漫迷。",
  },
  {
    year: "2022",
    title: "用户体验升级",
    description: "全面升级播放器和界面设计，支持高清画质和多设备适配，移动端观看体验大幅提升。",
  },
  {
    year: "2023",
    title: "社区成长",
    description: "月活跃用户突破百万，建立了活跃的动漫爱好者社区，为动漫文化的传播贡献力量。",
  },
  {
    year: "2024",
    title: "内容深化",
    description: "推出深度资讯栏目，从新番推荐到行业分析，为用户提供更丰富的动漫相关内容。",
  },
  {
    year: "2025",
    title: "持续创新",
    description: "持续优化平台体验，探索AI推荐等新技术，致力于成为中文动漫爱好者最信赖的在线平台。",
  },
];

const advantages = [
  {
    icon: "ri-movie-line",
    title: "海量资源",
    desc: "收录数万部动漫作品，从经典老番到最新连载全面覆盖，总有你想看的。",
  },
  {
    icon: "ri-hd-line",
    title: "高清画质",
    desc: "提供720P和1080P高清画质，精心优化的视频编码，流畅不卡顿的观影体验。",
  },
  {
    icon: "ri-speed-up-line",
    title: "极速更新",
    desc: "与日本同步更新热门连载番剧，第一时间为中文用户提供中文字幕版本。",
  },
  {
    icon: "ri-device-line",
    title: "多端适配",
    desc: "响应式设计完美适配电脑、平板和手机，随时随地享受动漫带来的快乐。",
  },
];

const contactInfo = [
  {
    icon: "ri-mail-line",
    title: "电子邮箱",
    content: "contact@naishuidongman.com",
  },
  {
    icon: "ri-global-line",
    title: "官方网站",
    content: "www.naishuidongman.com",
  },
  {
    icon: "ri-time-line",
    title: "工作时间",
    content: "周一至周五 9:00 - 18:00",
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus("success");
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete("website_alt");

      const response = await fetch("https://readdy.ai/api/form/d9sn5c58o1vn02ekui9g", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络连接后重试");
      setFormStatus("error");
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://naishuidongman.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "关于我们",
      },
    ],
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于奶水动漫在线",
    "description": "奶水动漫在线是领先的免费高清动漫在线观看平台，为动漫爱好者提供海量优质动漫资源。",
    "url": "https://naishuidongman.com/about",
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "奶水动漫在线",
    "url": "https://naishuidongman.com",
    "email": "contact@naishuidongman.com",
    "description": "免费高清动漫在线观看平台",
    "foundingDate": "2020",
  };

  return (
    <>
      <JsonLD data={breadcrumbSchema} />
      <JsonLD data={aboutSchema} />
      <JsonLD data={contactSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

            {/* Hero Section */}
            <section className="py-14 md:py-20 text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                关于奶水动漫在线
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                致力于为每一位动漫爱好者提供最优质的在线观看体验，让动漫的魅力触手可及。
              </p>
            </section>

            {/* Brand Introduction */}
            <section className="pb-14 md:pb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      奶水动漫在线成立于2020年，是国内领先的
                      <strong>免费高清动漫在线观看平台</strong>
                      。我们汇聚了海内外优质动漫资源，涵盖
                      <strong>热门番剧</strong>、
                      <strong>经典动漫</strong>、
                      <strong>最新连载</strong>
                      等丰富内容，致力于为中文动漫爱好者打造一站式的在线观看体验。
                    </p>
                    <p>
                      我们的团队由一群资深的动漫爱好者和互联网技术专家组成。我们深知动漫迷对于画质、更新速度和观看体验的高要求，因此在每一个环节都精益求精——从资源筛选、画质优化到界面设计，力求为用户提供最好的服务。
                    </p>
                    <p>
                      无论你是热血少年漫的忠实粉丝、甜宠恋爱番的爱好者，还是喜欢探索奇幻世界的冒险家，在奶水动漫在线都能找到属于自己的那份精彩。我们相信，动漫不仅仅是娱乐，更是一种连接人与人之间的文化纽带。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20anime%20studio%20office%20interior%20with%20colorful%20anime%20posters%20on%20walls%2C%20comfortable%20workspace%20with%20warm%20lighting%2C%20creative%20team%20environment%2C%20Japanese%20anime%20culture%20vibe%2C%20cozy%20atmosphere%2C%20professional%20but%20playful%20setting&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="奶水动漫在线品牌介绍 - 专业的动漫在线观看平台"
                    title="奶水动漫在线 - 专业的动漫在线观看平台"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Timeline */}
            <section className="pb-14 md:pb-20">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  发展历程
                </h2>
                <p className="text-sm text-foreground-600 mt-2">
                  一路走来的成长足迹
                </p>
              </div>
              <div className="max-w-4xl mx-auto">
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2 hidden md:block"></div>
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-background-200 md:hidden"></div>
                  <div className="space-y-8">
                    {timelineEvents.map((event, index) => {
                      const isEven = index % 2 === 0;
                      return (
                        <div key={index} className="relative flex flex-col md:flex-row gap-4">
                          <div className={`md:w-1/2 pl-10 md:pl-0 md:px-8 ${isEven ? "" : "md:order-2"}`}>
                            <div className={`bg-background-50 rounded-xl p-5 border border-background-200/70 ${isEven ? "md:text-right" : "md:text-left"}`}>
                              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-bold mb-2">
                                {event.year}
                              </span>
                              <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">
                                {event.title}
                              </h3>
                              <p className="text-sm text-foreground-600 leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          </div>
                          <div className="absolute left-[calc(1rem-6px)] md:left-1/2 md:-translate-x-1/2 top-6 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                          <div className={`md:w-1/2 hidden md:block ${isEven ? "" : "md:order-1"}`}></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation / Advantages */}
            <section className="pb-14 md:pb-20 bg-background-100/70 -mx-4 md:-mx-6 px-4 md:px-6 py-14 md:py-20">
              <div className="max-w-[1200px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    我们的优势
                  </h2>
                  <p className="text-sm text-foreground-600 mt-2">
                    为什么选择奶水动漫在线
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {advantages.map((item, index) => (
                    <div
                      key={index}
                      className="bg-background-50 rounded-xl p-5 border border-background-200/70 text-center"
                    >
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="py-14 md:py-20">
              <div className="max-w-[1000px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 mt-2">
                    有任何问题或建议？欢迎随时与我们联系
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                  <div className="lg:col-span-1 space-y-4">
                    {contactInfo.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-4 bg-background-50 rounded-xl p-4 border border-background-200/70"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-500 mb-0.5">{item.title}</p>
                          <p className="text-sm text-foreground-800 font-medium">{item.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="lg:col-span-2">
                    <form
                      id="contact-form"
                      data-readdy-form
                      className="bg-background-50 rounded-xl p-6 border border-background-200/70 space-y-4"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleFormSubmit(e);
                      }}
                    >
                      {formStatus === "success" && (
                        <div className="p-4 rounded-lg bg-secondary-100 text-secondary-800 text-sm">
                          <i className="ri-checkbox-circle-line mr-1.5"></i>
                          感谢您的留言！我们会在收到后尽快回复您。
                        </div>
                      )}
                      {formStatus === "error" && (
                        <div className="p-4 rounded-lg bg-accent-100 text-accent-800 text-sm">
                          <i className="ri-error-warning-line mr-1.5"></i>
                          {formError || "提交失败，请稍后再试"}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名
                          </label>
                          <input
                            type="text"
                            id="contact-name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱
                          </label>
                          <input
                            type="email"
                            id="contact-email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="contact-subject"
                          name="subject"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                          placeholder="请输入您想说的内容（最多500字）"
                        ></textarea>
                      </div>
                      <div className="website-alt-wrapper">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === "submitting"}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {formStatus === "submitting" ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            发送中...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line"></i>
                            发送留言
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}